import React, { useEffect, useRef, useState } from "react";
import { IPromoCode, IPromoCodeCreate, IPromoCodeUpdate } from "../models/IPromoCode";
import { getPromoCodesByRestaurant, postPromoCode, putPromoCode } from "../services/promoCode";

interface Props { restaurantId: string; showHeader?: boolean; }

const TODAY = new Date().toISOString().slice(0, 10);

function DateInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const display = value ? `${value.slice(8, 10)}/${value.slice(5, 7)}/${value.slice(0, 4)}` : "";
  return (
    <div style={{ position: "relative" }}>
      <div
        onClick={() => ref.current?.showPicker?.()}
        style={{
          padding: "9px 12px", borderRadius: 10, border: "1.5px solid #e5e7eb",
          fontSize: "0.88rem", background: "#fff", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          color: value ? "#111827" : "#9ca3af", userSelect: "none",
        }}
      >
        <span>{display || "GG/AA/YYYY"}</span>
        <i className="bi bi-calendar3" style={{ color: "#7c3aed", fontSize: "0.95rem" }}></i>
      </div>
      <input
        ref={ref}
        type="date"
        min={TODAY}
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{ position: "absolute", inset: 0, opacity: 0, width: "100%", height: "100%", cursor: "pointer" }}
      />
    </div>
  );
}

function fmtDate(iso: string) {
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
}
function isExpired(iso: string) { return new Date(iso) < new Date(); }
function calcPct(cur: number, max: number) { return max > 0 ? Math.min(100, Math.round((cur / max) * 100)) : 0; }

const empty: IPromoCodeCreate = {
  code: "", description: "", discountType: "Percentage",
  discountValue: 10, minOrderAmount: 0,
  maxDiscountAmount: null, maxUseCount: 100,
  maxUsePerCustomer: 1, expiryDate: "",
  restourantId: "",
};

export function PromoCodesContent({ restaurantId, showHeader = true }: Props) {
  const [list, setList]       = useState<IPromoCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [form, setForm]       = useState<IPromoCodeCreate>({ ...empty, restourantId: restaurantId });
  const [errors, setErrors]   = useState<Record<string, string>>({});
  const [saveErr, setSaveErr] = useState("");

  const [editItem, setEditItem]   = useState<IPromoCode | null>(null);
  const [editForm, setEditForm]   = useState<IPromoCodeUpdate>({ description: "", minOrderAmount: 0, maxDiscountAmount: null, maxUseCount: 1, maxUsePerCustomer: 1, expiryDate: "", isActive: true });
  const [editSaving, setEditSaving] = useState(false);
  const [editErr, setEditErr]     = useState("");

  useEffect(() => {
    if (!restaurantId) return;
    setForm(f => ({ ...f, restourantId: restaurantId }));
    load();
  }, [restaurantId]);

  const load = () => {
    setLoading(true);
    getPromoCodesByRestaurant(restaurantId)
      .then(res => setList(res.data ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const openAdd = () => {
    setForm({ ...empty, restourantId: restaurantId });
    setErrors({});
    setSaveErr("");
    setShowAdd(true);
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.code.trim())            e.code = "Kod zorunludur.";
    if (form.discountType === "Percentage" && (form.discountValue <= 0 || form.discountValue > 100))
      e.discountValue = "0 ile 100 arası girin.";
    if (form.discountType === "Fixed" && form.discountValue <= 0)
      e.discountValue = "0'dan büyük olmalı.";
    if (form.minOrderAmount < 0)      e.minOrderAmount = "0 veya üzeri girin.";
    if (form.maxUseCount < 1)         e.maxUseCount = "En az 1 olmalı.";
    if (form.maxUsePerCustomer < 1)   e.maxUsePerCustomer = "En az 1 olmalı.";
    if (!form.expiryDate)             e.expiryDate = "Tarih seçin.";
    else if (form.expiryDate < TODAY) e.expiryDate = "Geçmiş tarih seçilemez.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    setSaving(true);
    setSaveErr("");
    const payload: IPromoCodeCreate = {
      ...form,
      code: form.code.trim().toUpperCase(),
      expiryDate: form.expiryDate + "T23:59:59.000Z",
      maxDiscountAmount: form.maxDiscountAmount != null && String(form.maxDiscountAmount) !== "" ? Number(form.maxDiscountAmount) : null,
    };
    postPromoCode(payload)
      .then(() => { setShowAdd(false); load(); })
      .catch(() => setSaveErr("Kayıt başarısız, lütfen tekrar deneyin."))
      .finally(() => setSaving(false));
  };

  const set = (field: keyof IPromoCodeCreate, value: any) =>
    setForm(f => ({ ...f, [field]: value }));

  const openEdit = (p: IPromoCode) => {
    setEditItem(p);
    setEditForm({
      description: p.description,
      minOrderAmount: p.minOrderAmount,
      maxDiscountAmount: p.maxDiscountAmount,
      maxUseCount: p.maxUseCount,
      maxUsePerCustomer: p.maxUsePerCustomer,
      expiryDate: p.expiryDate.slice(0, 10),
      isActive: p.isActive,
    });
    setEditErr("");
  };

  const setEdit = (field: keyof IPromoCodeUpdate, value: any) =>
    setEditForm(f => ({ ...f, [field]: value }));

  const handleUpdate = () => {
    if (!editItem) return;
    setEditSaving(true);
    setEditErr("");
    const payload: IPromoCodeUpdate = {
      ...editForm,
      expiryDate: editForm.expiryDate + "T23:59:59.000Z",
      maxDiscountAmount: editForm.maxDiscountAmount != null && String(editForm.maxDiscountAmount) !== "" ? Number(editForm.maxDiscountAmount) : null,
    };
    putPromoCode(editItem.id, payload)
      .then(() => { setEditItem(null); load(); })
      .catch(() => setEditErr("Güncelleme başarısız, lütfen tekrar deneyin."))
      .finally(() => setEditSaving(false));
  };

  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "9px 12px", borderRadius: 10,
    border: "1.5px solid #e5e7eb", fontSize: "0.88rem",
    outline: "none", boxSizing: "border-box", color: "#111827",
  };
  const errStyle: React.CSSProperties = { fontSize: "0.72rem", color: "#dc2626", marginTop: 3 };
  const labelStyle: React.CSSProperties = { fontSize: "0.76rem", fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 };

  return (
    <div style={{ padding: "24px", maxWidth: 900, margin: "0 auto" }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
        {showHeader ? (
          <div>
            <h4 style={{ fontWeight: 800, color: "#111827", marginBottom: 4 }}>
              <i className="bi bi-tag-fill me-2" style={{ color: "#7c3aed" }}></i>Promosyon Kodları
            </h4>
            <p style={{ color: "#6b7280", fontSize: "0.88rem", margin: 0 }}>
              Restoranınızın indirim kuponlarını yönetin.
            </p>
          </div>
        ) : <div />}
        <button onClick={openAdd} style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "10px 20px", borderRadius: 12, border: "none",
          background: "linear-gradient(135deg,#7c3aed,#6d28d9)",
          color: "#fff", fontWeight: 700, fontSize: "0.88rem",
          cursor: "pointer", boxShadow: "0 4px 14px rgba(124,58,237,.4)",
        }}>
          <i className="bi bi-plus-circle-fill"></i>Promosyon Kodu Ekle
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <div className="spinner-border" style={{ color: "#7c3aed" }} role="status" />
          <p style={{ color: "#6b7280", marginTop: 12 }}>Yükleniyor...</p>
        </div>
      ) : list.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#9ca3af" }}>
          <i className="bi bi-tag" style={{ fontSize: "2.5rem", display: "block", marginBottom: 10 }}></i>
          <span style={{ fontSize: "0.9rem" }}>Henüz promosyon kodu eklenmemiş.</span>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {list.map(p => {
            const expired   = isExpired(p.expiryDate);
            const full      = p.currentUseCount >= p.maxUseCount;
            const active    = p.isActive && !expired && !full;
            const pct       = calcPct(p.currentUseCount, p.maxUseCount);
            const remaining = p.maxUseCount - p.currentUseCount;
            return (
              <div key={p.id} onClick={() => openEdit(p)} style={{
                background: "#fff", borderRadius: 16,
                border: `1.5px solid ${active ? "#ede9fe" : "#f1f5f9"}`,
                boxShadow: "0 2px 10px rgba(0,0,0,.05)", overflow: "hidden",
                cursor: "pointer", transition: "box-shadow 0.15s",
              }}
              onMouseEnter={e => (e.currentTarget.style.boxShadow = "0 4px 18px rgba(124,58,237,.15)")}
              onMouseLeave={e => (e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,.05)")}
              >
                {/* Top stripe */}
                <div style={{
                  height: 4,
                  background: active ? "linear-gradient(90deg,#7c3aed,#6d28d9)" : "#e5e7eb",
                }} />
                <div style={{ padding: "14px 18px", display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>

                  {/* Code + description */}
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: "1.15rem", fontWeight: 900, letterSpacing: "2px", color: "#111827" }}>{p.code}</span>
                      <span style={{
                        fontSize: "0.68rem", fontWeight: 700, padding: "2px 8px", borderRadius: 20,
                        background: p.discountType === "Percentage" ? "#ede9fe" : "#dbeafe",
                        color: p.discountType === "Percentage" ? "#6d28d9" : "#1d4ed8",
                      }}>
                        {p.discountType === "Percentage" ? `%${p.discountValue} İndirim` : `${p.discountValue} TL İndirim`}
                      </span>
                      <span style={{
                        fontSize: "0.65rem", fontWeight: 700, padding: "2px 8px", borderRadius: 20,
                        background: active ? "#f0fdf4" : "#fef2f2",
                        color: active ? "#059669" : "#dc2626",
                      }}>
                        {expired ? "Süresi Doldu" : !p.isActive ? "Pasif" : full ? "Kota Doldu" : "Aktif"}
                      </span>
                    </div>
                    {p.description && (
                      <div style={{ fontSize: "0.8rem", color: "#6b7280" }}>{p.description}</div>
                    )}
                  </div>

                  {/* Stats */}
                  <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                    {/* Usage */}
                    <div style={{ minWidth: 130 }}>
                      <div style={{ fontSize: "0.7rem", color: "#9ca3af", marginBottom: 4 }}>Kullanım</div>
                      <div style={{ background: "#f3f4f6", borderRadius: 6, height: 6, marginBottom: 4 }}>
                        <div style={{ width: `${pct}%`, height: "100%", background: pct >= 100 ? "#ef4444" : pct >= 80 ? "#f59e0b" : "#7c3aed", borderRadius: 6 }} />
                      </div>
                      <div style={{ fontSize: "0.72rem", color: "#374151", fontWeight: 600 }}>
                        {p.currentUseCount}/{p.maxUseCount}
                        <span style={{ color: "#9ca3af", fontWeight: 400, marginLeft: 4 }}>({remaining} kaldı)</span>
                      </div>
                    </div>

                    {/* Details */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                      <div style={{ fontSize: "0.72rem", color: "#6b7280" }}>
                        <i className="bi bi-cart-check me-1"></i>Min. sipariş: <b>{p.minOrderAmount > 0 ? `${p.minOrderAmount} TL` : "Yok"}</b>
                      </div>
                      {p.maxDiscountAmount != null && p.maxDiscountAmount > 0 && (
                        <div style={{ fontSize: "0.72rem", color: "#6b7280" }}>
                          <i className="bi bi-arrow-down-circle me-1"></i>Max sipariş: <b>{p.maxDiscountAmount} TL</b>
                        </div>
                      )}
                      <div style={{ fontSize: "0.72rem", color: "#6b7280" }}>
                        <i className="bi bi-person me-1"></i>Kişi başı: <b>{p.maxUsePerCustomer} kez</b>
                      </div>
                      <div style={{ fontSize: "0.72rem", color: expired ? "#dc2626" : "#6b7280" }}>
                        <i className="bi bi-calendar-x me-1"></i>Bitiş: <b>{fmtDate(p.expiryDate)} 23:59</b>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Popup */}
      {showAdd && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1050, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(3px)", overflowY: "auto", padding: "20px 0" }}
          onClick={e => { if (e.target === e.currentTarget) setShowAdd(false); }}
        >
          <div style={{ background: "#fff", borderRadius: 20, width: "min(96vw,500px)", boxShadow: "0 8px 40px rgba(0,0,0,0.2)", overflow: "hidden" }}>

            {/* Popup header */}
            <div style={{ background: "linear-gradient(135deg,#7c3aed,#6d28d9)", padding: "18px 22px", color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className="bi bi-tag-fill" style={{ fontSize: "1rem" }}></i>
                </div>
                <span style={{ fontWeight: 800, fontSize: "1rem" }}>Promosyon Kodu Ekle</span>
              </div>
              <button onClick={() => setShowAdd(false)} style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8, color: "#fff", cursor: "pointer", width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div style={{ padding: "22px", display: "flex", flexDirection: "column", gap: 16 }}>

              {/* Kod & Açıklama */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={labelStyle}>Kod <span style={{ color: "#dc2626" }}>*</span></label>
                  <input
                    value={form.code}
                    onChange={e => set("code", e.target.value.toUpperCase())}
                    placeholder="ÖR: YAZA20"
                    style={{ ...inputStyle, textTransform: "uppercase", letterSpacing: "1px", fontWeight: 700, borderColor: errors.code ? "#fca5a5" : "#e5e7eb" }}
                  />
                  {errors.code && <div style={errStyle}>{errors.code}</div>}
                </div>
                <div>
                  <label style={labelStyle}>Açıklama</label>
                  <input
                    value={form.description}
                    onChange={e => set("description", e.target.value)}
                    placeholder="Kısa açıklama"
                    style={inputStyle}
                  />
                </div>
              </div>

              {/* İndirim tipi */}
              <div>
                <label style={labelStyle}>İndirim Tipi <span style={{ color: "#dc2626" }}>*</span></label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {(["Percentage", "Fixed"] as const).map(type => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => { set("discountType", type); set("discountValue", type === "Percentage" ? 10 : 50); }}
                      style={{
                        padding: "10px 8px", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: "0.85rem",
                        border: form.discountType === type ? "2px solid #7c3aed" : "2px solid #e5e7eb",
                        background: form.discountType === type ? "#ede9fe" : "#f9fafb",
                        color: form.discountType === type ? "#6d28d9" : "#6b7280",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                      }}
                    >
                      <i className={type === "Percentage" ? "bi bi-percent" : "bi bi-currency-dollar"}></i>
                      {type === "Percentage" ? "Yüzdelik (%)" : "Sabit (TL)"}
                    </button>
                  ))}
                </div>
              </div>

              {/* İndirim değeri */}
              <div>
                <label style={labelStyle}>
                  İndirim Değeri <span style={{ color: "#dc2626" }}>*</span>
                  <span style={{ fontWeight: 400, color: "#9ca3af", marginLeft: 4 }}>
                    {form.discountType === "Percentage" ? "(0-100 arası)" : "(TL cinsinden)"}
                  </span>
                </label>
                <div style={{ position: "relative" }}>
                  <input
                    type="number"
                    min={0.01}
                    max={form.discountType === "Percentage" ? 100 : undefined}
                    step={form.discountType === "Percentage" ? 1 : 0.01}
                    value={form.discountValue}
                    onChange={e => set("discountValue", Number(e.target.value))}
                    style={{ ...inputStyle, paddingRight: 40, borderColor: errors.discountValue ? "#fca5a5" : "#e5e7eb" }}
                  />
                  <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontWeight: 700, color: "#7c3aed", fontSize: "0.9rem" }}>
                    {form.discountType === "Percentage" ? "%" : "TL"}
                  </span>
                </div>
                {errors.discountValue && <div style={errStyle}>{errors.discountValue}</div>}
              </div>

              {/* Limitler */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={labelStyle}>
                    Min. Sipariş Tutarı <span style={{ color: "#dc2626" }}>*</span>
                  </label>
                  <div style={{ position: "relative" }}>
                    <input
                      type="number" min={0} step={1}
                      value={form.minOrderAmount}
                      onChange={e => set("minOrderAmount", Number(e.target.value))}
                      style={{ ...inputStyle, paddingRight: 32, borderColor: errors.minOrderAmount ? "#fca5a5" : "#e5e7eb" }}
                    />
                    <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontWeight: 700, color: "#9ca3af", fontSize: "0.82rem" }}>TL</span>
                  </div>
                  {errors.minOrderAmount && <div style={errStyle}>{errors.minOrderAmount}</div>}
                </div>
                <div>
                  <label style={labelStyle}>
                    Max. Sipariş Tutarı
                    <span style={{ fontWeight: 400, color: "#9ca3af", marginLeft: 4 }}>(opsiyonel)</span>
                  </label>
                  <div style={{ position: "relative" }}>
                    <input
                      type="number" min={0} step={1}
                      value={form.maxDiscountAmount ?? ""}
                      onChange={e => set("maxDiscountAmount", e.target.value === "" ? null : Number(e.target.value))}
                      placeholder="Sınırsız"
                      style={{ ...inputStyle, paddingRight: 32 }}
                    />
                    <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontWeight: 700, color: "#9ca3af", fontSize: "0.82rem" }}>TL</span>
                  </div>
                </div>
              </div>

              {/* Kullanım limitleri */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={labelStyle}>Toplam Kullanım Limiti <span style={{ color: "#dc2626" }}>*</span></label>
                  <input
                    type="number" min={1} step={1}
                    value={form.maxUseCount}
                    onChange={e => set("maxUseCount", Number(e.target.value))}
                    style={{ ...inputStyle, borderColor: errors.maxUseCount ? "#fca5a5" : "#e5e7eb" }}
                  />
                  {errors.maxUseCount && <div style={errStyle}>{errors.maxUseCount}</div>}
                </div>
                <div>
                  <label style={labelStyle}>Kişi Başı Limit <span style={{ color: "#dc2626" }}>*</span></label>
                  <input
                    type="number" min={1} step={1}
                    value={form.maxUsePerCustomer}
                    onChange={e => set("maxUsePerCustomer", Number(e.target.value))}
                    style={{ ...inputStyle, borderColor: errors.maxUsePerCustomer ? "#fca5a5" : "#e5e7eb" }}
                  />
                  {errors.maxUsePerCustomer && <div style={errStyle}>{errors.maxUsePerCustomer}</div>}
                </div>
              </div>

              {/* Son kullanım tarihi */}
              <div>
                <label style={labelStyle}>Son Kullanım Tarihi <span style={{ color: "#dc2626" }}>*</span></label>
                <DateInput
                  value={form.expiryDate}
                  onChange={v => set("expiryDate", v)}
                />
                {form.expiryDate && !errors.expiryDate && (
                  <div style={{ fontSize: "0.72rem", color: "#7c3aed", marginTop: 4, fontWeight: 600 }}>
                    <i className="bi bi-clock me-1"></i>
                    {fmtDate(form.expiryDate)} saat 23:59'a kadar geçerli olacak.
                  </div>
                )}
                {errors.expiryDate && <div style={errStyle}>{errors.expiryDate}</div>}
              </div>

              {saveErr && (
                <div style={{ background: "#fef2f2", border: "1.5px solid #fecaca", borderRadius: 10, padding: "8px 12px", color: "#b91c1c", fontSize: "0.82rem" }}>
                  <i className="bi bi-exclamation-circle me-1"></i>{saveErr}
                </div>
              )}
            </div>

            {/* Butonlar */}
            <div style={{ display: "flex", gap: 10, padding: "0 22px 22px", justifyContent: "flex-end" }}>
              <button onClick={() => setShowAdd(false)} style={{ padding: "9px 18px", borderRadius: 10, border: "1.5px solid #e5e7eb", background: "#fff", color: "#6b7280", fontWeight: 600, fontSize: "0.85rem", cursor: "pointer" }}>
                İptal
              </button>
              <button onClick={handleSave} disabled={saving} style={{
                padding: "9px 22px", borderRadius: 10, border: "none",
                background: "linear-gradient(135deg,#7c3aed,#6d28d9)",
                color: "#fff", fontWeight: 700, fontSize: "0.85rem",
                cursor: saving ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", gap: 7,
                opacity: saving ? 0.7 : 1,
              }}>
                {saving
                  ? <><span className="spinner-border spinner-border-sm"></span>Kaydediliyor...</>
                  : <><i className="bi bi-check-lg"></i>Kaydet</>
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Popup */}
      {editItem && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1050, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(3px)", overflowY: "auto", padding: "20px 0" }}
          onClick={e => { if (e.target === e.currentTarget) setEditItem(null); }}
        >
          <div style={{ background: "#fff", borderRadius: 20, width: "min(96vw,500px)", boxShadow: "0 8px 40px rgba(0,0,0,0.2)", overflow: "hidden" }}>

            {/* Header */}
            <div style={{ background: "linear-gradient(135deg,#7c3aed,#6d28d9)", padding: "18px 22px", color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className="bi bi-pencil-fill" style={{ fontSize: "1rem" }}></i>
                </div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: "1rem" }}>{editItem.code}</div>
                  <div style={{ fontSize: "0.75rem", opacity: 0.8 }}>
                    {editItem.discountType === "Percentage" ? `%${editItem.discountValue} indirim` : `${editItem.discountValue} TL indirim`}
                  </div>
                </div>
              </div>
              <button onClick={() => setEditItem(null)} style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8, color: "#fff", cursor: "pointer", width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div style={{ padding: "22px", display: "flex", flexDirection: "column", gap: 16 }}>

              {/* Açıklama */}
              <div>
                <label style={labelStyle}>Açıklama</label>
                <input value={editForm.description} onChange={e => setEdit("description", e.target.value)} placeholder="Kısa açıklama" style={inputStyle} />
              </div>

              {/* Min / Max tutar */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={labelStyle}>Min. Sipariş Tutarı <span style={{ color: "#dc2626" }}>*</span></label>
                  <div style={{ position: "relative" }}>
                    <input type="number" min={0} step={1} value={editForm.minOrderAmount} onChange={e => setEdit("minOrderAmount", Number(e.target.value))} style={{ ...inputStyle, paddingRight: 32 }} />
                    <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontWeight: 700, color: "#9ca3af", fontSize: "0.82rem" }}>TL</span>
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Max. Sipariş Tutarı <span style={{ fontWeight: 400, color: "#9ca3af" }}>(opsiyonel)</span></label>
                  <div style={{ position: "relative" }}>
                    <input type="number" min={0} step={1} value={editForm.maxDiscountAmount ?? ""} onChange={e => setEdit("maxDiscountAmount", e.target.value === "" ? null : Number(e.target.value))} placeholder="Sınırsız" style={{ ...inputStyle, paddingRight: 32 }} />
                    <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontWeight: 700, color: "#9ca3af", fontSize: "0.82rem" }}>TL</span>
                  </div>
                </div>
              </div>

              {/* Kullanım limitleri */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={labelStyle}>Toplam Kullanım Limiti <span style={{ color: "#dc2626" }}>*</span></label>
                  <input type="number" min={1} step={1} value={editForm.maxUseCount} onChange={e => setEdit("maxUseCount", Number(e.target.value))} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Kişi Başı Limit <span style={{ color: "#dc2626" }}>*</span></label>
                  <input type="number" min={1} step={1} value={editForm.maxUsePerCustomer} onChange={e => setEdit("maxUsePerCustomer", Number(e.target.value))} style={inputStyle} />
                </div>
              </div>

              {/* Son kullanım tarihi */}
              <div>
                <label style={labelStyle}>Son Kullanım Tarihi <span style={{ color: "#dc2626" }}>*</span></label>
                <DateInput value={editForm.expiryDate} onChange={v => setEdit("expiryDate", v)} />
                {editForm.expiryDate && (
                  <div style={{ fontSize: "0.72rem", color: "#7c3aed", marginTop: 4, fontWeight: 600 }}>
                    <i className="bi bi-clock me-1"></i>{fmtDate(editForm.expiryDate)} saat 23:59'a kadar geçerli.
                  </div>
                )}
              </div>

              {/* Aktif toggle */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#f8faff", borderRadius: 12, padding: "12px 16px", border: "1.5px solid #e0e7ff" }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "#111827" }}>Kupon Aktif</div>
                  <div style={{ fontSize: "0.72rem", color: "#6b7280" }}>Kapalıysa kupon kullanılamaz</div>
                </div>
                <button
                  type="button"
                  onClick={() => setEdit("isActive", !editForm.isActive)}
                  style={{
                    width: 46, height: 26, borderRadius: 13, border: "none", cursor: "pointer",
                    background: editForm.isActive ? "#7c3aed" : "#d1d5db",
                    position: "relative", transition: "background 0.2s",
                  }}
                >
                  <span style={{
                    position: "absolute", top: 3, left: editForm.isActive ? 23 : 3,
                    width: 20, height: 20, borderRadius: "50%", background: "#fff",
                    transition: "left 0.2s", boxShadow: "0 1px 4px rgba(0,0,0,.2)",
                  }} />
                </button>
              </div>

              {editErr && (
                <div style={{ background: "#fef2f2", border: "1.5px solid #fecaca", borderRadius: 10, padding: "8px 12px", color: "#b91c1c", fontSize: "0.82rem" }}>
                  <i className="bi bi-exclamation-circle me-1"></i>{editErr}
                </div>
              )}
            </div>

            {/* Butonlar */}
            <div style={{ display: "flex", gap: 10, padding: "0 22px 22px", justifyContent: "flex-end" }}>
              <button onClick={() => setEditItem(null)} style={{ padding: "9px 18px", borderRadius: 10, border: "1.5px solid #e5e7eb", background: "#fff", color: "#6b7280", fontWeight: 600, fontSize: "0.85rem", cursor: "pointer" }}>
                İptal
              </button>
              <button onClick={handleUpdate} disabled={editSaving} style={{
                padding: "9px 22px", borderRadius: 10, border: "none",
                background: "linear-gradient(135deg,#7c3aed,#6d28d9)",
                color: "#fff", fontWeight: 700, fontSize: "0.85rem",
                cursor: editSaving ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", gap: 7,
                opacity: editSaving ? 0.7 : 1,
              }}>
                {editSaving
                  ? <><span className="spinner-border spinner-border-sm"></span>Kaydediliyor...</>
                  : <><i className="bi bi-check-lg"></i>Güncelle</>
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
